// components/BrandFooter/FooterContent.tsx — BrandFooter/index.tsx split (render body)
import React from 'react';
import { MapPinIcon, MailIcon, WhatsAppIcon, ClockIcon, HandshakeIcon } from './icons';
import { getOptimizedImageUrl } from '../../utils/imageUtils';

export interface FooterCollab {
  label: string;
  path: string;
}

export interface FooterPolicyLink {
  label: string;
  path: string;
}

interface FooterContentProps {
  data: {
    businessName: string;
    logo: string;
    address: string;
    contactEmail: string;
    contactPhone: string;
    operationHours: string;
    policies: { nib?: string };
  };
  collab: FooterCollab;
  policyLinks: FooterPolicyLink[];
  isMY?: boolean;
}

const brandSlug = (name: string) => name.toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/(^-|-$)/g, '');

export const FooterContent: React.FC<FooterContentProps> = ({ data, collab, policyLinks, isMY }) => {
  const slug = brandSlug(data.businessName);

  return (
    <div className="bg-white dark:bg-slate-800 border-t border-slate-200 dark:border-slate-700 px-4 py-6">
      <div className="flex flex-col items-center gap-2.5 text-xs text-slate-500 dark:text-slate-400">
        <div className="flex items-center gap-2.5">
          {data.logo ? (
            <img src={getOptimizedImageUrl(data.logo, { width: 200, quality: 80 }) ?? data.logo} alt={data.businessName} loading="lazy" decoding="async" className="w-9 h-9 rounded-full object-cover border border-slate-200 dark:border-slate-700" onError={e => { e.currentTarget.style.display = 'none'; }} />
          ) : (
            <div className="w-9 h-9 rounded-full bg-gradient-to-br from-indigo-500 to-indigo-700 text-white flex items-center justify-center text-base font-bold">
              {(data.businessName || '?').charAt(0).toUpperCase()}
            </div>
          )}
          <span className="font-semibold text-sm text-slate-800 dark:text-slate-100 tracking-wide uppercase">{data.businessName}</span>
        </div>

        {data.policies.nib && <p className="text-[11px] text-slate-500 dark:text-slate-400">NIB: {data.policies.nib}</p>}

        {data.address && (
          <p className="flex items-start justify-center gap-1.5 max-w-sm mx-auto text-[11px] leading-relaxed">
            <MapPinIcon />
            <span>{data.address}</span>
          </p>
        )}

        <div className="flex items-center justify-center gap-3">
          <a href={`/p/${slug}/kontak`} className="inline-flex items-center gap-1.5 text-indigo-600 dark:text-indigo-400 hover:underline">
            <MailIcon /> <span>{isMY ? 'Emel CS' : 'Email CS'}</span>
          </a>
          <a href={`/p/${slug}/kontak`} className="inline-flex items-center gap-1.5 text-indigo-600 dark:text-indigo-400 hover:underline">
            <WhatsAppIcon /> <span>WhatsApp CS</span>
          </a>
          <a href={`/p/${slug}/${collab.path}`} className="inline-flex items-center gap-1.5 text-indigo-600 dark:text-indigo-400 hover:underline">
            <HandshakeIcon /> <span>{collab.label}</span>
          </a>
        </div>

        <p className="flex items-center justify-center gap-1.5">
          <ClockIcon />
          <span>{data.operationHours}</span>
        </p>

        <div className="w-full border-t border-slate-100 dark:border-slate-700 pt-2.5" />
        <p className="flex flex-wrap items-center justify-center gap-x-3 gap-y-1">
          {policyLinks.map(link => (
            <a key={link.label} href={`/p/${slug}/${link.path}`} className="text-indigo-600 dark:text-indigo-400 hover:underline">
              {link.label}
            </a>
          ))}
        </p>

        <p className="text-[10px] text-slate-500 dark:text-slate-400">{`© ${new Date().getFullYear()} ${data.businessName}. ${isMY ? 'Hak cipta terpelihara.' : 'Hak cipta dilindungi.'}`}</p>
      </div>
    </div>
  );
};
